import { useState } from 'react'

import { useAuth } from '@clerk/nextjs'
import { ArrowRightIcon, CopyIcon } from '@radix-ui/react-icons'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import { GrafbaseThemes } from 'style'

import { dashboardPaths } from 'domains/website/website.paths'
import useClipboard from 'hooks/use-clipboard'
import { features } from 'lib/constants/features'
import { getDocsQuickStartPath } from 'lib/routes/replace-dynamic-paths'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import { IconStyled } from 'components/atomic/atoms/icon'
import { Database as DatabaseIcon } from 'components/atomic/atoms/icons/database'
import Text from 'components/atomic/atoms/text'
import CodeBlock from 'components/atomic/molecules/code-block'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import Divider from 'components/pages/@shared/divider'
import { ArrowsIcon } from 'components/pages/features/edge-caching/icons/arrows-icon'
import { ChartIcon } from 'components/pages/features/edge-caching/icons/chart-icon'
import { FieldIcon } from 'components/pages/features/edge-caching/icons/field-icon'
import { RulesIcon } from 'components/pages/features/edge-caching/icons/rules-icon'
import { SpeedIcon } from 'components/pages/features/edge-caching/icons/speed-icon'
import { TimeIcon } from 'components/pages/features/edge-caching/icons/time-icon'
import { TypeIcon } from 'components/pages/features/edge-caching/icons/type-icon'
import {
  HomepageSectionSubtitle,
  HomepageSectionTitle,
  SectionTitleContainer,
} from 'components/pages/index/@shared/homepage-section-title'

import FaunaCover from '../../public/images/guides/build-and-deploy-a-graphql-api-to-the-edge-with-fauna/cover.png'
import CouchbaseCover from '../../public/images/guides/deploy-graphql-apis-at-the-edge-with-couchbase/cover.png'
import CommercetoolsCover from '../../public/images/guides/graphql-edge-caching-commercetools/cover.png'
import ContentfulCover from '../../public/images/guides/graphql-edge-caching-contentful.png'
import DatocmsCover from '../../public/images/guides/graphql-edge-caching-datocms.png'
import DgraphCover from '../../public/images/guides/graphql-edge-caching-dgraph-cloud/graphql-edge-caching-dgraph-cloud.png'
import OpenaiCover from '../../public/images/guides/graphql-edge-caching-openai.png'
import ShopifyCover from '../../public/images/guides/graphql-edge-caching-shopify.png'
import StoryblokCover from '../../public/images/guides/graphql-edge-caching-storyblok.png'
import StrapiCover from '../../public/images/guides/graphql-edge-caching-strapi.png'
import StripeCover from '../../public/images/guides/graphql-edge-caching-stripe.png'
import TinybirdCover from '../../public/images/guides/graphql-edge-caching-tinybird.png'

const CHART_DATA = [
  { date: 0, red: 1967, green: 802 },
  { date: 1, red: 2063, green: 901 },
  { date: 2, red: 2420, green: 1088 },
  { date: 3, red: 1805, green: 1505 },
  { date: 4, red: 2444, green: 3919 },
  { date: 5, red: 1280, green: 4937 },
  { date: 6, red: 1820, green: 5048 },
  { date: 7, red: 1272, green: 3057 },
  { date: 8, red: 1852, green: 5705 },
  { date: 9, red: 1630, green: 5758 },
  { date: 10, red: 1636, green: 4354 },
  { date: 11, red: 1370, green: 5202 },
  { date: 12, red: 1433, green: 5523 },
  { date: 13, red: 1150, green: 5889 },
]

const FEATURES = [
  {
    title: 'Improve Performance',
    description: 'Delight your end users with blazing fast API response times.',
    icon: SpeedIcon,
  },
  {
    title: 'Reduce Load',
    description: 'Reduce load to your origin by caching at the edge.',
    icon: ChartIcon,
  },
  {
    title: 'Cache Anything',
    description:
      'Make your databases, REST APIs and GraphQL APIs globally fast.',
    icon: DatabaseIcon,
  },
]

const CODE_EXAMPLES = [
  {
    title: 'OpenAPI Edge Caching',
    description: 'Improve performance and scalability of REST APIs.',
    code: {
      ts: `
import { graph, config, connector } from '@grafbase/sdk'

const g = graph.Standalone()

const openai = connector.OpenAPI('OpenAI', {
  schema: 'https://raw.githubusercontent.com/openai/openai-openapi/master/openapi.yaml',
  headers: (headers) => {
    headers.set('Authorization', \`Bearer \${g.env('OPENAI_API_KEY')}\`)
  }
})

g.datasource(openai)

export default config({
  graph: g,
  cache: {
    rules: [
      {
        types: 'Query',
        maxAge: 60,
        staleWhileRevalidate: 60,
      }
    ]
  }
})
      `,
    },
  },
  {
    title: 'GraphQL Edge Caching',
    description:
      'Give your existing GraphQL APIs a boost and cache responses at the edge.',
    code: {
      ts: `
import { graph, config, connector } from '@grafbase/sdk'

const g = graph.Standalone()

const contentful = connector.GraphQL('Contentful', {
  url: g.env('CONTENTFUL_API_URL'),
  headers: (headers) => {
    headers.set('Authorization', \`Bearer \${g.env('CONTENTFUL_API_TOKEN')}\`)
  }
})

g.datasource(contentful)

export default config({
  graph: g,
  cache: {
    rules: [
      {
        types: 'Query',
        maxAge: 60,
        staleWhileRevalidate: 60,
      }
    ]
  }
})
      `,
    },
  },
  {
    title: 'MongoDB Edge Caching',
    description: 'Deploy GraphQL APIs for MongoDB and cache them at the edge.',
    code: {
      ts: `
import { config, connector, graph } from '@grafbase/sdk'

const g = graph.Standalone()

const mongo = connector.MongoDB('MongoDB', {
  apiKey: g.env('MONGODB_API_KEY'),
  url: g.env('MONGODB_API_URL'),
  dataSource: g.env('MONGODB_DATASOURCE'),
  database: g.env('MONGODB_DATABASE')
})

mongo.model('User', {
  name: g.string(),
  age: g.int().optional()
}).collection('users')

g.datasource(mongo)

export default config({
  graph: g,
  cache: {
    rules: [
      {
        types: 'Query',
        maxAge: 60
      }
    ]
  }
})
      `,
    },
  },
]

const CARDS_EXAMPLES = [
  {
    title: 'Global rules',
    description: 'Configure globally to cache everything at the edge.',
    href: '/docs/graphql-edge-caching/config#global',
    icon: RulesIcon,
  },
  {
    title: 'Types',
    description: 'Configure cache rules when declaring individual types.',
    href: '/docs/graphql-edge-caching/rules#types',
    icon: TypeIcon,
  },
  {
    title: 'Fields',
    description:
      'Configure cache rules for each field, including data from other sources with resolvers.',
    href: '/docs/graphql-edge-caching/rules#field',
    icon: FieldIcon,
  },
  {
    title: 'Max Age',
    description:
      'Configure how long query results should be cached, in seconds.',
    href: '/docs/graphql-edge-caching/rules#maxage',
    icon: TimeIcon,
  },
  {
    title: 'Stale-While-Revalidate',
    description:
      'Configure how long query results should be delivered while new data is being refreshed.',
    href: '/docs/graphql-edge-caching/rules#stalewhilerevalidate',
    icon: ArrowsIcon,
  },
]

const templates = [
  {
    image: ShopifyCover,
    name: 'Shopify',
    description:
      'Build faster commerce experiences with Shopify and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-shopify',
  },
  {
    image: CommercetoolsCover,
    name: 'commercetools',
    description:
      'Build faster omnichannel experiences with commercetools and Edge Caching.',
    href: '/guides/graphql-edge-caching-commercetools',
  },
  {
    image: StripeCover,
    name: 'Stripe',
    description:
      "Transform Stripe's REST API to GraphQL and add GraphQL Edge Caching.",
    href: '/guides/graphql-edge-caching-stripe',
  },
  {
    image: ContentfulCover,
    name: 'Contentful',
    description:
      'Build faster captivating experiences with Contentful and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-contentful',
  },
  {
    image: StoryblokCover,
    name: 'Storyblok',
    description:
      'Build faster content experiences with Storyblok and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-storyblok',
  },
  {
    image: StrapiCover,
    name: 'Strapi Cloud',
    description:
      'Deliver content faster with with Strapi Cloud and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-strapi-cloud',
  },
  {
    image: DatocmsCover,
    name: 'DatoCMS',
    description:
      'Build faster content experiences with DatoCMS and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-datocms',
  },
  {
    image: FaunaCover,
    name: 'Fauna',
    description:
      'Build faster applications with Fauna and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-fauna',
  },
  {
    image: DgraphCover,
    name: 'Dgraph Cloud',
    description:
      'Build faster web and mobile apps with Dgraph and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-dgraph-cloud',
  },
  {
    image: CouchbaseCover,
    name: 'Couchbase',
    description:
      'Build faster applications with Couchbase and GraphQL Edge Caching.',
    href: '/guides/deploy-graphql-apis-at-the-edge-with-couchbase',
  },
  {
    image: TinybirdCover,
    name: 'Tinybird',
    description:
      'Reduce API requests to your data pipes with Tinybird and GraphQL Edge Caching.',
    href: '/guides/graphql-edge-caching-tinybird',
  },
  {
    image: OpenaiCover,
    name: 'OpenAI',
    description:
      "Transform OpenAI's REST API to GraphQL and add GraphQL Edge Caching.",
    href: '/guides/graphql-edge-caching-openai',
  },
]

const TabsControlled = () => {
  const { hasCopied, onCopy } = useClipboard()
  const [value, setValue] = useState<(typeof CODE_EXAMPLES)[number]['title']>(
    CODE_EXAMPLES[0].title,
  )
  const [language] = useState<'ts' | 'graphql'>('ts')
  return (
    <TabsPrimitive.Root value={value} asChild>
      <Box
        flex={{ '@initial': 'column', '@md': 'rowReversed' }}
        css={{ gap: '$2', '@md': { gap: '$8', width: '$full' } }}
      >
        <TabsPrimitive.List asChild>
          <Box
            flex={{ '@initial': true, '@md': 'column' }}
            css={{
              gap: '$2',
              pb: '$2',
              overflowX: 'scroll',
              '@md': {
                width: 380,
                maxWidth: 380,
                minWidth: 380,
                gap: '$6',
                py: '$8',
                overflowX: 'auto',
              },
            }}
          >
            {CODE_EXAMPLES.map(({ title, description }) => (
              <TabsPrimitive.Trigger key={title} value={title} asChild>
                <Box
                  as="button"
                  onClick={() => setValue(title)}
                  onMouseEnter={() => setValue(title)}
                  flex="column"
                  css={{
                    p: '$2-5 $6',
                    gap: '$2',
                    textAlign: 'left',
                    borderRadius: '$lg',
                    bgc: '$transparent',
                    transition: 'background-color 100ms ease',
                    '&:hover, &[data-state="active"]': {
                      bgc: '$bg2',
                    },
                    '&:focus': {
                      outline: 'none',
                    },
                    '@md': {
                      p: '$6 $5',
                    },
                  }}
                >
                  <Text
                    size={{ '@initial': 'md', '@md': 'xl' }}
                    weight={{
                      '@initial': 'regular',
                      '@md': 'semiBold',
                    }}
                    css={{ whiteSpace: 'nowrap' }}
                  >
                    {title}
                  </Text>
                  <Text
                    color="text2"
                    css={{
                      display: 'none',
                      '@md': { display: 'block' },
                    }}
                  >
                    {description}
                  </Text>
                </Box>
              </TabsPrimitive.Trigger>
            ))}
          </Box>
        </TabsPrimitive.List>
        <Box
          flex="column"
          css={{
            overflowX: 'auto',
            '@md': {
              width: '$full',
              minHeight: '$full',
            },
          }}
        >
          <Box
            flex
            items="center"
            justify="between"
            css={{
              p: '$2 $4',
              bgc: '$bgCard',
              borderTopLeftRadius: '$lg',
              borderTopRightRadius: '$lg',
              border: '1px solid $borderCard',
              borderBottom: 0,
            }}
          >
            <Text size="sm" color="text2" css={{ fontFamily: '$mono' }}>
              {language === 'ts' ? 'grafbase.config.ts' : 'schema.graphql'}
            </Text>
          </Box>
          <Box
            css={{
              height: language === 'ts' ? 610 : 430,
              bgc: '$bgCard',
              border: '1px solid $borderCard',
            }}
          >
            {CODE_EXAMPLES.map(({ title, code }) => (
              <TabsPrimitive.Content key={title} value={title} asChild>
                <Box
                  relative
                  css={{
                    height: '$full',
                    '& .code-line': { border: 'none' },
                  }}
                >
                  <CodeBlock
                    language={language}
                    showLineNumbers={false}
                    css={{ py: '$6' }}
                  >
                    {code[language]}
                  </CodeBlock>
                  <Box
                    as="button"
                    data-copy
                    aria-label="Copy code to clipboard"
                    onClick={() => onCopy(code[language])}
                    absolute
                    css={{
                      opacity: 0,
                      right: '$4',
                      top: '$4',
                      p: '$1-5',
                      border: '1px solid',
                      borderColor: hasCopied ? '$reversed' : '#504F57',
                      background: '#161618',
                      borderRadius: '$md',
                      transition: '.2s ease-in',
                      '&:hover': {
                        bgc: '$bg1',
                      },
                    }}
                  >
                    <IconStyled
                      as={CopyIcon}
                      css={{
                        color: hasCopied ? '$reversed' : '#504F57',
                        transition: '.2s ease-in',
                      }}
                    />
                  </Box>
                </Box>
              </TabsPrimitive.Content>
            ))}
          </Box>
        </Box>
      </Box>
    </TabsPrimitive.Root>
  )
}

const EdgeCachingPage = () => {
  const { asPath } = useRouter()
  const { isSignedIn } = useAuth()

  return (
    <LayoutWebsite
      meta={{
        title: 'Reduce latency and improve conversions globally',
        description:
          'Cache your GraphQL API at the edge to make it lightning fast and protect your backend from traffic spikes.',
        imageUrl: '/images/og/edge-caching.png',
      }}
      paddingTop={false}
      paddingBottom={false}
    >
      <Box
        css={{
          bgc: '$base',
          $$lightGreen: '#A3DCC3',
          $$lightRed: '#F6A89B',
        }}
      >
        <Box as="section" relative flex="column" css={{ zIndex: 0 }}>
          <Box
            relative
            flex="column"
            justify={{ '@initial': 'start', '@md': 'center' }}
            items="center"
            css={{
              zIndex: 1,
              gap: '$4',
              mt: '$28',
              mb: '$18',
              '@md': {
                mt: '$40',
                mb: '$28',
              },
            }}
          >
            <Box css={{ container: 'xl', mx: 'auto', spaceY: 6 }} textCenter>
              <Text
                as="h1"
                weight="bold"
                css={{
                  fontSize: 48,
                  lineHeight: 0.9,
                  letterSpacing: '$tighter',
                  '@lg': {
                    fontSize: 72,
                    lineHeight: 1,
                  },
                }}
              >
                Reduce latency and improve conversions
              </Text>
              <Text
                as="p"
                color="text2"
                size={{ '@initial': 'md', '@sm': 'lg' }}
              >
                Cache your GraphQL API at the edge to make it lightning fast and
                protect your backend from traffic spikes.
              </Text>
            </Box>
            <Box relative css={{ zIndex: 0, width: '$full' }}>
              {/* -2px – otherwise some devices show horizontal scrollbar */}
              <Box css={{ height: 450, width: 'calc(100vw-2px)', mx: 'auto' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={CHART_DATA}
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="analytics-gradient-green"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="30%"
                          stopOpacity={0.35}
                          stopColor="var(---lightGreen)"
                        />
                        <stop
                          offset="100%"
                          stopOpacity={0}
                          stopColor="var(---lightGreen)"
                        />
                      </linearGradient>
                      <linearGradient
                        id="analytics-gradient-red"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="30%"
                          stopOpacity={0.35}
                          stopColor="var(---lightRed)"
                        />
                        <stop
                          offset="100%"
                          stopOpacity={0}
                          stopColor="var(---lightRed)"
                        />
                      </linearGradient>
                    </defs>
                    <Area
                      dataKey="red"
                      type="basis"
                      stackId="1"
                      strokeWidth={2}
                      stroke="var(---lightRed)"
                      fill={`url(#analytics-gradient-red)`}
                      isAnimationActive={false}
                    />
                    <Area
                      dataKey="green"
                      type="basis"
                      stackId="1"
                      strokeWidth={2}
                      stroke="var(---lightGreen)"
                      fill={`url(#analytics-gradient-green)`}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
              <Box
                grid
                css={{
                  container: 'xl',
                  mx: 'auto',
                  gridCols: 1,
                  gap: '$10',
                  '@md': {
                    gridCols: 3,
                  },
                }}
              >
                {FEATURES.map(({ title, description, icon }) => (
                  <Box key={title} flex="column" css={{ flex: 1, gap: '$3' }}>
                    <Box flex items="center" css={{ gap: '$4' }}>
                      <Box
                        flex
                        items="center"
                        justify="center"
                        css={{
                          p: '$2',
                          borderRadius: '$xl',
                          bgc: '#E3BC98',
                          [`.${GrafbaseThemes.dark} &`]: {
                            bgc: '#674A2E',
                          },
                        }}
                      >
                        <IconStyled
                          as={icon}
                          css={{
                            size: 25,
                            color: '#000000',
                            [`.${GrafbaseThemes.dark} &`]: {
                              color: '$textAccent',
                            },
                          }}
                        />
                      </Box>
                      <Text
                        as="p"
                        weight="semiBold"
                        css={{
                          fontSize: '$2xl',
                          lineHeight: '$3xl',
                        }}
                      >
                        {title}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" color="text2">
                        {description}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box
                absolute
                css={{
                  isolation: 'isolate',
                  inset: 0,
                  zIndex: -1,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  filter: 'blur(100px)',
                  background: '',
                  [`.${GrafbaseThemes.dark} &`]: {
                    background:
                      'linear-gradient(90deg, rgba(226, 155, 91, 0.5) 0%, rgba(112, 68, 28, 0.5) 10%)',
                  },
                }}
              />
            </Box>
          </Box>
          <Box
            absolute
            css={{
              isolation: 'isolate',
              inset: 0,
              zIndex: 0,
              opacity: 0.6,
              overflow: 'hidden',
              pointerEvents: 'none',
              userSelect: 'none',
              '@-moz-document url-prefix()': {
                filter: 'blur(100px)',
              },
            }}
          >
            <Box
              absolute
              css={{
                width: 450,
                height: 700,
                zIndex: -2,
                top: '0%',
                left: 'calc(45% - 450px)',
                filter: 'blur(100px)',
                borderRadius: '$round',
                mixBlendMode: 'lighten',
                transform: 'translate3d(0, 0, 0) rotate(45deg)',
                background:
                  'linear-gradient(182deg, #FFCEA0 6.04%, rgba(255, 234, 216, 0.00) 97.97%)',
                [`.${GrafbaseThemes.dark} &`]: {
                  background:
                    'linear-gradient(180deg, rgba(205,128,57,1) 0%, rgba(155, 100, 140, 0) 100%)',
                },
              }}
            />
            <Box
              absolute
              css={{
                size: 500,
                zIndex: -1,
                top: '50%',
                left: '50%',
                filter: 'blur(100px)',
                borderRadius: '$round',
                mixBlendMode: 'screen',
                transform: 'translate3d(0, 0, 0) translate(-50%, -50%)',
                background:
                  'linear-gradient(182deg, #FFCEA0 6.04%, rgba(255, 234, 216, 0.00) 97.97%)',
                [`.${GrafbaseThemes.dark} &`]: {
                  background: 'rgba(205,128,57,.5)',
                },
              }}
            />
            <Box
              absolute
              css={{
                width: 450,
                height: 700,
                zIndex: -3,
                top: '0%',
                right: 'calc(45% - 450px)',
                filter: 'blur(100px)',
                borderRadius: '$round',
                mixBlendMode: 'screen',
                transform: 'translate3d(0, 0, 0) rotate(-45deg)',
                background:
                  'linear-gradient(182deg, #FFCEA0 6.04%, rgba(255, 234, 216, 0.00) 97.97%)',
                [`.${GrafbaseThemes.dark} &`]: {
                  background:
                    'linear-gradient(180deg, rgba(205,128,57,1) 0%, rgba(116, 81, 153, 0) 100%)',
                },
              }}
            />
          </Box>
        </Box>
        <Box as="section" relative css={{ zIndex: 1 }}>
          <Box
            css={{
              container: 'xl',
              spaceY: 16,
              py: '$20',
              '@md': {
                py: '$40',
              },
            }}
          >
            <Box css={{ mx: 'auto', spaceY: 3 }} textCenter>
              <Text
                as="h2"
                weight="bold"
                size={{ '@initial': '4xl', '@md': '5xl' }}
              >
                Unleash the Power of Edge Caching
              </Text>
              <Text
                as="p"
                color="text2"
                size="xl"
                css={{
                  mt: '$6',
                }}
              >
                Add edge caching to any API or database in minutes.
              </Text>
            </Box>
            <TabsControlled />
            <Box textCenter>
              <Text
                as={Link}
                href={
                  isSignedIn ? getDocsQuickStartPath() : dashboardPaths.register
                }
                colorHover="text2"
                css={{
                  fontSize: '$lg',
                }}
              >
                Get started for free{' '}
                <IconStyled as={ArrowRightIcon} css={{ display: 'inline' }} />
              </Text>
            </Box>
          </Box>
        </Box>
        <Divider
          lineBg="linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(220, 220, 220, 1) 50%, rgba(255, 255, 255, 0) 100%)"
          lineBgDark="linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(89, 89, 89, 1) 50%, rgba(0, 0, 0, 1) 100%)"
          bg="linear-gradient(250deg, rgba(177, 255, 218, 0) 10%, rgba(221, 221, 221, 0.25) 50%, rgba(177, 255, 218, 0) 90%)"
          bgDark="linear-gradient(250deg, rgba(9, 9, 9, 1) 10%, rgba(229, 229, 229, 0.25) 50%, rgba(9, 9, 9, 1) 90%)"
        />
        <Box as="section" relative css={{ zIndex: 1 }}>
          <Box
            css={{
              container: 'xl',
              spaceY: 16,
              py: '$20',
              '@md': {
                py: '$40',
              },
            }}
          >
            <Box css={{ mx: 'auto', spaceY: 3 }} textCenter>
              <Text
                as="h2"
                weight="bold"
                size={{ '@initial': '4xl', '@md': '5xl' }}
              >
                The most configurable GraphQL edge cache
              </Text>
            </Box>
            <Box
              grid
              css={{
                gridCols: 1,
                gap: '$10',
                '@md': {
                  gridCols: 3,
                  columnGap: '$10',
                  rowGap: '$12',
                },
              }}
            >
              {CARDS_EXAMPLES.map(({ title, description, href, icon }) => (
                <Box
                  key={title}
                  flex="column"
                  css={{
                    padding: '$6 $5',
                    borderRadius: '$lg',
                    bgc: '$bg2',
                    '.dark &': {
                      bgc: '$bg1',
                    },
                  }}
                >
                  <Box flex="column" css={{ flex: 1, gap: '$3' }}>
                    <Box flex items="center" css={{ gap: '$4' }}>
                      <Box
                        flex
                        items="center"
                        justify="center"
                        css={{
                          p: '$2',
                          borderRadius: '$md',
                          background:
                            'linear-gradient(100deg, #414A60 0%, #3A3E4F 100%)',
                        }}
                      >
                        <IconStyled
                          as={icon}
                          color="textAccent"
                          css={{ size: 25 }}
                        />
                      </Box>
                      <Text
                        as="p"
                        weight="semiBold"
                        css={{
                          fontSize: '$2xl',
                          lineHeight: '$3xl',
                        }}
                      >
                        {title}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" size="sm" color="text3">
                        {description}
                      </Text>
                    </Box>
                    <Box css={{ mt: 'auto' }}>
                      <Text
                        as={Link}
                        href={href}
                        colorHover="text2"
                        css={{
                          fontSize: '$sm',
                          lineHeight: '$md',
                        }}
                      >
                        Learn More{' '}
                        <IconStyled
                          as={ArrowRightIcon}
                          css={{ display: 'inline' }}
                        />
                      </Text>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Divider
          lineBg="linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(220, 220, 220, 1) 50%, rgba(255, 255, 255, 0) 100%)"
          lineBgDark="linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(89, 89, 89, 1) 50%, rgba(0, 0, 0, 1) 100%)"
          bg="linear-gradient(250deg, rgba(177, 255, 218, 0) 10%, rgba(221, 221, 221, 0.25) 50%, rgba(177, 255, 218, 0) 90%)"
          bgDark="linear-gradient(250deg, rgba(9, 9, 9, 1) 10%, rgba(229, 229, 229, 0.25) 50%, rgba(9, 9, 9, 1) 90%)"
        />
        <Box
          css={{
            container: 'xl',
            py: '$20',
            '@md': {
              py: '$40',
            },
          }}
        >
          <SectionTitleContainer>
            <HomepageSectionTitle>
              Ready to deploy schema templates
            </HomepageSectionTitle>

            <HomepageSectionSubtitle>
              Browse our growing collection of GraphQL Edge Caching templates to
              get started.
            </HomepageSectionSubtitle>
          </SectionTitleContainer>

          <Box
            flex
            items="center"
            justify="center"
            css={{ my: '$12', gap: '$4' }}
          >
            <a
              href="https://github.com/grafbase/grafbase/tree/main/templates"
              target="_blank"
              rel="noopener"
            >
              <Button variant="secondary">GitHub Library</Button>
            </a>
          </Box>
          <Box
            relative
            css={{
              container: 'xl',
            }}
          >
            <Box grid css={{ '@md': { gridCols: 3 }, gap: '$8' }}>
              {templates.map(({ image, description, name, href }) => (
                <Box
                  key={name}
                  css={{
                    bgc: '$bg2',
                    borderRadius: '$xl',
                    border: '1px solid $bg3',
                  }}
                >
                  <Box
                    relative
                    css={{
                      width: '$full',
                      height: 200,
                      borderRadiusTop: '$xl',
                      overflow: 'hidden',
                    }}
                  >
                    <Image
                      alt={name}
                      src={image}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  </Box>
                  <Box css={{ p: '$6' }}>
                    <Text as="h3" size="lg" weight="bold">
                      {name}
                    </Text>
                    <Text as="p" color="text3" css={{ mt: '$2' }}>
                      {description}
                    </Text>
                    <Box css={{ mt: '$6', spaceX: 2 }}>
                      <Link href={href} target="_blank" rel="noopener">
                        <Button variant="secondary">View Guide</Button>
                      </Link>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Divider
          lineBg="linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(220, 220, 220, 1) 50%, rgba(255, 255, 255, 0) 100%)"
          lineBgDark="linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(89, 89, 89, 1) 50%, rgba(0, 0, 0, 1) 100%)"
          bg="linear-gradient(250deg, rgba(177, 255, 218, 0) 10%, rgba(221, 221, 221, 0.25) 50%, rgba(177, 255, 218, 0) 90%)"
          bgDark="linear-gradient(250deg, rgba(9, 9, 9, 1) 10%, rgba(229, 229, 229, 0.25) 50%, rgba(9, 9, 9, 1) 90%)"
        />
        <Box as="section">
          <Box
            css={{
              container: 'xl',
              spaceY: 16,
              pt: '$20',
              '@md': {
                pt: '$40',
              },
            }}
          >
            <Box
              css={{
                mx: 'auto',
                spaceY: 3,
              }}
              textCenter
            >
              <Text
                as="h2"
                weight="bold"
                size={{ '@initial': '4xl', '@md': '5xl' }}
              >
                Explore other features
              </Text>
            </Box>
            <Box
              flex
              justify="center"
              css={{
                flexWrap: 'wrap',
                gap: '$6',
                '@md': {
                  gap: '$10',
                },
              }}
            >
              {features
                .filter(({ href }) => href !== asPath)
                .map(({ name, description, icon, href }) => (
                  <Box
                    key={name}
                    flex="column"
                    css={{
                      width: 380,
                      gap: '$1',
                      padding: '$6 $5',
                      borderRadius: '$lg',
                      bgc: '$bgCard',
                    }}
                  >
                    <Box flex items="center" css={{ gap: '$3' }}>
                      <Box>
                        <IconStyled
                          as={icon}
                          color="text1"
                          css={{ size: 32 }}
                        />
                      </Box>
                      <Text
                        as="p"
                        weight="semiBold"
                        css={{
                          fontSize: '$2xl',
                          lineHeight: '$3xl',
                        }}
                      >
                        {name}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" color="text3">
                        {description}
                      </Text>
                    </Box>
                    <Box css={{ mt: 'auto', pt: '$4' }}>
                      <Text as={Link} href={href} colorHover="text2" size="sm">
                        Learn More{' '}
                        <IconStyled
                          as={ArrowRightIcon}
                          css={{ display: 'inline' }}
                        />
                      </Text>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
        <Box
          as="section"
          css={{
            height: 200,
            background: 'linear-gradient(to bottom, $base 0%, $bg1 100%)',
          }}
        ></Box>
      </Box>
    </LayoutWebsite>
  )
}

export default EdgeCachingPage
